import { useState } from 'react';
import { Heading } from './Heading';
import { spacingClass } from '@/utils/spacing';

const inputCls = 'w-full rounded-lg border border-theme-border bg-theme-bg-surface px-4 py-3 text-sm text-theme-text outline-none focus:border-theme-accent transition-colors';

/**
 * Modulo contatti — invia via server/contact.js (SMTP o Brevo, scelto in
 * Impostazioni → Modulo contatti). Nessun redirect: submit via fetch,
 * feedback inline immediato.
 * @param {{ title?: string, subtitle?: string, namePlaceholder?: string, emailPlaceholder?: string, messagePlaceholder?: string, buttonLabel?: string, successMessage?: string, headingLevel?: number }} props
 */
export function ContactFormBlock({
  title, subtitle,
  namePlaceholder = 'Nome',
  emailPlaceholder = 'Email',
  messagePlaceholder = 'Messaggio',
  buttonLabel = 'Invia',
  successMessage = 'Messaggio inviato — ti risponderemo presto.',
  spacing = 'normal',
  headingLevel = 2,
}) {
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' }); // website = honeypot
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, message: form.message, honeypot: form.website }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Errore durante l\'invio.');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  }

  return (
    <section className={'px-6 md:px-16 ' + spacingClass(spacing)}>
      <div className="max-w-lg mx-auto">
        {(title || subtitle) && (
          <div className="text-center mb-9">
            {title && <Heading level={headingLevel} className="font-heading text-h1 font-light text-theme-text mb-4">{title}</Heading>}
            {subtitle && <p className="text-body-lg text-theme-text-muted leading-relaxed">{subtitle}</p>}
          </div>
        )}

        {status === 'success' ? (
          <p role="status" className="text-center text-sm text-theme-accent font-medium">{successMessage}</p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
            <input type="text" required placeholder={namePlaceholder} value={form.name} onChange={(e) => update('name', e.target.value)} className={inputCls} />
            <input type="email" required placeholder={emailPlaceholder} value={form.email} onChange={(e) => update('email', e.target.value)} className={inputCls} />
            <textarea required rows={5} placeholder={messagePlaceholder} value={form.message} onChange={(e) => update('message', e.target.value)} className={inputCls + ' resize-y'} />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide disabled:opacity-50 transition-opacity"
            >
              {status === 'loading' ? '...' : buttonLabel}
            </button>
            {status === 'error' && <p role="alert" className="text-xs text-red-500 text-center">{error}</p>}
          </form>
        )}
      </div>
    </section>
  );
}
